import { catchError, filter, first, from, Observable, switchMap, throwError } from 'rxjs';
import { fromFetch } from 'rxjs/fetch';
import { snackbarError } from '@yabble/yabble-component-library/services';
import { tokens } from 'services/tokens';
import { getUserStore } from 'stores/user.store';
import { SseParser } from 'utils/sse';
import { convertKeysToCamel, convertKeysToSnake, JsonValue } from 'utils/stringCase';
import { ApiTypes, isApiError, isWikiEmptyError } from './api.types';

export { isApiError, isWikiEmptyError } from './api.types';
export type { ApiTypes } from './api.types';

/**
 * Single root for `compliance-api`. The default keeps every call on the app's own origin
 * under `/api`, which `vite.config.ts` proxies in development and the reverse proxy
 * handles in a deployed environment; the env var is the escape hatch for pointing a
 * local UI at a remote API.
 */
const API_ROOT = import.meta.env.VITE_API_ROOT || '/api';

const defaultMessage = (status: number) => {
  switch (status) {
    case 401:
      return 'Your session has expired. Sign in again.';
    case 403:
      return 'You do not have permission to do that.';
    case 404:
      return 'That is no longer there.';
    case 409:
      return 'That conflicts with something already in progress.';
    case 413:
      return 'That file is too large.';
    default:
      return status >= 500
        ? 'Something went wrong on our end. Try again shortly.'
        : 'The request could not be completed.';
  }
};

const buildUrl = (url: string, options: ApiTypes.Options) => {
  const target = options.raw
    ? new URL(url)
    : new URL(`${API_ROOT.replace(/\/$/, '')}/${url.replace(/^\//, '')}`, window.location.origin);

  if (options.searchParams) {
    const params = options.skipCaseConversion
      ? options.searchParams
      : (convertKeysToSnake(options.searchParams as JsonValue) as ApiTypes.SearchParams);

    target.search = new URLSearchParams(
      Object.entries(params)
        .filter(([, value]) => value !== null && value !== undefined && value !== '')
        .map(([key, value]) => [key, String(value)])
    ).toString();
  }

  return target.toString();
};

/** A body the browser serialises itself — a file upload or a multipart form. */
const isRawBody = (body: unknown): body is BodyInit =>
  body instanceof Blob || body instanceof FormData || body instanceof ArrayBuffer;

const buildHeaders = (token: string | null, body: unknown, options: ApiTypes.Options, accept: string) => {
  const headers = new Headers({ Accept: accept });

  /** Let the browser set `Content-Type` (and the multipart boundary) for a raw body. */
  if (body !== null && body !== undefined && !isRawBody(body)) {
    headers.set('Content-Type', 'application/json');
  }

  /**
   * Never attach our token to a `raw` URL — those point at third parties (the pre-signed
   * S3 `PUT`), and a pre-signed URL rejects requests that carry an `Authorization`
   * header anyway, because the header is not part of what was signed.
   */
  if (token && !options.raw) headers.set('Authorization', `Bearer ${token}`);

  Object.entries(options.headers ?? {}).forEach(([key, value]) => headers.set(key, value));

  return headers;
};

const serialiseBody = (body: unknown, options: ApiTypes.Options): BodyInit | undefined => {
  if (body === null || body === undefined) return undefined;
  if (isRawBody(body)) return body;
  return JSON.stringify(options.skipCaseConversion ? body : convertKeysToSnake(body as JsonValue));
};

const readSuccess = <T,>(res: Response, options: ApiTypes.Options): Promise<T> => {
  if (options.responseType === 'blob') return res.blob() as Promise<T>;
  if (options.responseType === 'empty' || res.status === 204) return Promise.resolve(undefined as T);

  return res.text().then((text) => {
    if (!text) return undefined as T;
    try {
      const parsed = JSON.parse(text);
      return (options.skipCaseConversion ? parsed : convertKeysToCamel(parsed)) as T;
    } catch {
      throw { status: res.status, message: 'The server sent a response we could not read.' };
    }
  });
};

const readFailure = (res: Response): Promise<ApiTypes.Error> =>
  res
    .text()
    .then((text) => {
      try {
        return text ? JSON.parse(text) : undefined;
      } catch {
        /** A gateway or proxy failure is HTML, not JSON. The status still tells us enough. */
        return undefined;
      }
    })
    .then((body) => {
      const detail = body?.detail;
      const message =
        (typeof detail === 'string' && detail) ||
        (typeof body?.message === 'string' && body.message) ||
        defaultMessage(res.status);
      return {
        status: res.status,
        message,
        detail,
        code: typeof body?.code === 'string' ? body.code : undefined,
      };
    });

/**
 * The single place a failure is acted on, which is why every call goes through this
 * module: a `401` drops the whole app to the unauthenticated branch, and everything else
 * surfaces to the user as a snackbar. Features handle the *absence* of data, not the
 * mechanics of the failure.
 *
 * The one exception is `wiki_empty`: no knowledge base yet is the expected state of a fresh
 * environment, not a fault, and the chat page answers it with an empty-state panel — a
 * snackbar on top would call it an error.
 */
const handleFailure = (error: ApiTypes.Error, options: ApiTypes.Options) => {
  if (error.status === 401) {
    tokens.clear();
    getUserStore().setUnauthorized();
  }
  if (!options.silent && !isWikiEmptyError(error)) snackbarError({ title: error.message });
  return error;
};

/** A rejected `fetch` — offline, DNS, CORS — never reached the server, hence status `0`. */
const networkError = (err: unknown): ApiTypes.Error => ({
  status: 0,
  message: err instanceof Error && err.message ? err.message : 'Could not reach the server.',
});

/** Waits for a real token before emitting; `'loading'` means auth has not resolved yet. */
const $withToken = <T,>(project: (token: string | null) => Observable<T>) =>
  tokens.$accessToken.pipe(
    filter((token): token is string | null => token !== 'loading'),
    first(),
    switchMap(project)
  );

const makeRequest = <T,>(
  method: string,
  url: string,
  body: unknown,
  options: ApiTypes.Options
): Observable<T> =>
  $withToken((token) =>
    fromFetch(
      new Request(buildUrl(url, options), {
        method,
        headers: buildHeaders(token, body, options, 'application/json'),
        body: serialiseBody(body, options),
        cache: 'no-cache',
      })
    ).pipe(
      switchMap((res) =>
        res.ok
          ? from(readSuccess<T>(res, options))
          : from(readFailure(res)).pipe(switchMap((error) => throwError(() => error)))
      ),
      catchError((err) => throwError(() => handleFailure(isApiError(err) ? err : networkError(err), options)))
    )
  );

export const $get = <T,>(url: string, params: ApiTypes.SearchParams = {}, options: ApiTypes.Options = {}) =>
  makeRequest<T>('GET', url, null, { ...options, searchParams: params });

export const $post = <T,>(url: string, body: unknown = {}, options: ApiTypes.Options = {}) =>
  makeRequest<T>('POST', url, body, options);

export const $put = <T,>(url: string, body: unknown = {}, options: ApiTypes.Options = {}) =>
  makeRequest<T>('PUT', url, body, options);

export const $delete = <T,>(url: string, body: unknown = null, options: ApiTypes.Options = {}) =>
  makeRequest<T>('DELETE', url, body, options);

export const $putFileWithProgress = (url: string, file: File): Observable<ApiTypes.UploadEvent> =>
  new Observable<ApiTypes.UploadEvent>((subscriber) => {
    const xhr = new XMLHttpRequest();

    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable)
        subscriber.next({ type: 'progress', loaded: event.loaded, total: event.total });
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        subscriber.next({ type: 'done' });
        subscriber.complete();
      } else {
        subscriber.error({ status: xhr.status, message: defaultMessage(xhr.status) });
      }
    };

    xhr.onerror = () => subscriber.error(networkError(new Error('The upload failed.')));

    xhr.send(file);

    return () => xhr.abort();
  });

/**
 * Reads a Server-Sent Events response as an observable of typed events.
 *
 * `EventSource` cannot do this job: the chat request needs a `POST` body and an
 * `Authorization` header, and `EventSource` supports neither. So the response body is
 * read directly, and `SseParser` handles the fact that chunk boundaries fall wherever the
 * network puts them (see `utils/sse`).
 *
 * - a `done` frame emits and then **completes** the observable;
 * - an `error` frame **errors** the observable rather than completing it quietly;
 * - unsubscribing aborts the in-flight request.
 */
export const $stream = (
  url: string,
  body: unknown = {},
  options: ApiTypes.Options = {}
): Observable<ApiTypes.StreamEvent> =>
  $withToken(
    (token) =>
      new Observable<ApiTypes.StreamEvent>((subscriber) => {
        const controller = new AbortController();
        let cancelled = false;

        const fail = (error: ApiTypes.Error) => {
          if (cancelled) return;
          subscriber.error(handleFailure(error, options));
        };

        const emit = (frame: { event: string; data: string }) => {
          if (cancelled) return true;

          let payload: any = {};
          try {
            payload = frame.data ? convertKeysToCamel(JSON.parse(frame.data)) : {};
          } catch {
            /** A frame we cannot parse is a contract violation, not a recoverable state. */
            fail({ status: 0, message: 'The server sent a malformed stream event.' });
            return false;
          }

          switch (frame.event) {
            case 'delta':
              subscriber.next({ type: 'delta', text: payload.text ?? '' });
              return true;
            case 'done':
              subscriber.next({
                type: 'done',
                success: payload.success ?? true,
                error: payload.error,
                citedPaths: payload.citedPaths ?? [],
                citations: payload.citations ?? [],
              });
              subscriber.complete();
              return false;
            case 'error':
              fail({ status: 0, message: payload.message || 'The answer failed part-way through.' });
              return false;
            default:
              /** Unknown event types are ignored, so the API can add some without breaking us. */
              return true;
          }
        };

        const run = async () => {
          const res = await fetch(
            new Request(buildUrl(url, options), {
              method: 'POST',
              headers: buildHeaders(token, body, options, 'text/event-stream'),
              body: serialiseBody(body, options),
              cache: 'no-cache',
              signal: controller.signal,
            })
          );

          if (!res.ok) return fail(await readFailure(res));

          if (!res.body) {
            return fail({ status: res.status, message: 'The server sent no stream to read.' });
          }

          const reader = res.body.getReader();
          const decoder = new TextDecoder();
          const parser = new SseParser();

          for (;;) {
            const { done, value } = await reader.read();

            if (done) {
              /** `stream: false` flushes any trailing bytes of a split multi-byte character. */
              const tail = parser.push(decoder.decode(undefined, { stream: false }));
              for (const frame of [...tail, ...parser.flush()]) if (!emit(frame)) return;
              /**
               * The stream ended without a `done` frame — the connection dropped
               * mid-answer. Completing here would look to a caller exactly like success.
               */
              return fail({ status: 0, message: 'The connection closed before the answer finished.' });
            }

            for (const frame of parser.push(decoder.decode(value, { stream: true }))) {
              if (!emit(frame)) return;
            }
          }
        };

        run().catch((err) => {
          /** An abort is our own teardown, not a failure worth reporting. */
          if (cancelled || controller.signal.aborted) return;
          fail(networkError(err));
        });

        return () => {
          cancelled = true;
          controller.abort();
        };
      })
  );
